/* ═══════════════════════════════════════════════════════════════════════
   EF Mobile Drawer — interaction layer
   • Open/close via data-ef-drawer-open / data-ef-mdrawer-close
   • Sub-panel slide nav via data-ef-mdrawer-open-sub="<id>"
   • Back button returns to main panel (data-ef-mdrawer-back)
   • Scroll-locks body while open; restores on close
   • Escape closes; tab-trap inside panel
   • Exposed API: window.EFMobileDrawer.open() / .close()
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var drawer = document.getElementById('ef-mobile-drawer');
  if (!drawer) return;

  /* Move drawer to <body> direct child so no section wrapper with transform/
     will-change can trap `position: fixed` in a local containing block. */
  if (drawer.parentElement && drawer.parentElement !== document.body) {
    document.body.appendChild(drawer);
  }

  var panel = drawer.querySelector('.ef-mdrawer__panel');
  var main = drawer.querySelector('.ef-mdrawer__main');
  var subs = drawer.querySelectorAll('[data-ef-mdrawer-sub]');
  var lastFocus = null;
  var scrollY = 0;

  function focusableIn(el) {
    return el.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  function lockScroll() {
    scrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('ef-mdrawer-open');
    document.body.classList.add('ef-mdrawer-open');
  }

  function unlockScroll() {
    document.documentElement.classList.remove('ef-mdrawer-open');
    document.body.classList.remove('ef-mdrawer-open');
    if (scrollY) window.scrollTo(0, scrollY);
  }

  function open() {
    if (drawer.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    // force reflow so transition fires
    drawer.offsetHeight; // eslint-disable-line no-unused-expressions
    drawer.classList.add('is-open');
    lockScroll();
    // focus first actionable element for keyboard users
    setTimeout(function () {
      var first = drawer.querySelector('.ef-mdrawer__close');
      if (first) first.focus();
    }, 60);
  }

  function close() {
    if (!drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.classList.remove('is-subpanel');
    drawer.setAttribute('aria-hidden', 'true');
    subs.forEach(function (s) {
      s.classList.remove('is-active');
      s.setAttribute('aria-hidden', 'true');
    });
    unlockScroll();
    setTimeout(function () {
      drawer.hidden = true;
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }, 320);
  }

  function openSub(id) {
    var sub = drawer.querySelector('[data-ef-mdrawer-sub="' + id + '"]');
    if (!sub) return;
    subs.forEach(function (s) {
      if (s !== sub) { s.classList.remove('is-active'); s.setAttribute('aria-hidden', 'true'); }
    });
    sub.classList.add('is-active');
    sub.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-subpanel');
    sub.scrollTop = 0;
    setTimeout(function () {
      var back = sub.querySelector('.ef-mdrawer__back');
      if (back) back.focus();
    }, 300);
  }

  function backToMain() {
    subs.forEach(function (s) {
      s.classList.remove('is-active');
      s.setAttribute('aria-hidden', 'true');
    });
    drawer.classList.remove('is-subpanel');
    setTimeout(function () {
      var first = drawer.querySelector('.ef-mdrawer__cat');
      if (first) first.focus();
    }, 300);
  }

  /* ─── Open triggers (anywhere on page) ─── */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-ef-drawer-open]');
    if (!trigger) return;
    e.preventDefault();
    open();
  });

  /* 2026-05-07 — expose close as global so inline handlers (Calendly, AI modal)
     can close drawer before opening their own UI. */
  window.efCloseDrawer = close;

  /* ─── Events inside the drawer ─── */
  drawer.addEventListener('click', function (e) {
    if (e.target.closest('[data-ef-mdrawer-close]')) {
      e.preventDefault();
      close();
      return;
    }
    var subTrigger = e.target.closest('[data-ef-mdrawer-open-sub]');
    if (subTrigger) {
      e.preventDefault();
      openSub(subTrigger.getAttribute('data-ef-mdrawer-open-sub'));
      return;
    }
    if (e.target.closest('[data-ef-mdrawer-back]')) {
      e.preventDefault();
      backToMain();
    }
    /* 2026-05-07 — auto-close drawer on regular link click. Prevents broken
       UX where drawer remained open over the navigated page. Skip if link
       is anchor-only (#) or has explicit handler. */
    var link = e.target.closest('a[data-ef-mdrawer-close-link]');
    if (link && link.getAttribute('href') && link.getAttribute('href') !== '#') {
      /* let navigation proceed; close drawer first so animation completes */
      close();
    }
  });

  /* ─── Keyboard ─── */
  document.addEventListener('keydown', function (e) {
    if (!drawer.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      // Escape on a sub-panel returns to main; Escape on main closes drawer
      if (drawer.classList.contains('is-subpanel')) backToMain();
      else close();
      return;
    }
    if (e.key === 'Tab') {
      var activeSub = drawer.querySelector('.ef-mdrawer__sub.is-active');
      var scope = activeSub || main;
      var focusables = focusableIn(panel);
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  /* ─── If viewport grows past desktop while drawer is open, close it ─── */
  var mq = window.matchMedia('(min-width: 1024px)');
  var onChange = function (ev) { if (ev.matches) close(); };
  if (mq.addEventListener) mq.addEventListener('change', onChange);
  else if (mq.addListener) mq.addListener(onChange);

  /* ─── Public API ─── */
  window.EFMobileDrawer = { open: open, close: close, openSub: openSub };
})();
